
import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RecordReview } from './RecordReview';
import { DisputeTracking } from './DisputeTracking';
import { DisputePacketGenerator } from './DisputePacketGenerator';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertTriangle, FileText, Clock, CheckCircle, Plus, List, ArrowRight } from 'lucide-react';

interface DisputesWorkflowProps {
  selectedPatientId?: string;
}

interface RecordItem {
  id: string;
  type: 'condition' | 'medication' | 'lab' | 'procedure';
  title: string;
  description: string;
  date: string;
  provider: string;
  flagged: boolean;
  reason?: string;
  severity: 'high' | 'medium' | 'low';
  fhirResource?: any;
}

export const DisputesWorkflow = ({ selectedPatientId = 'me' }: DisputesWorkflowProps) => {
  const [activeDisputes, setActiveDisputes] = useState(0);
  const [pendingReview, setPendingReview] = useState(0);
  const [completedDisputes, setCompletedDisputes] = useState(0);
  const [flaggedItems, setFlaggedItems] = useState<RecordItem[]>([]);
  const [activeTab, setActiveTab] = useState('create-dispute');
  const [disputeStep, setDisputeStep] = useState<'review' | 'generate'>('review');

  useEffect(() => {
    // Load dispute statistics
    loadDisputeStats();
  }, [selectedPatientId]);

  const loadDisputeStats = () => {
    // In a real app, this would fetch from an API
    setActiveDisputes(3);
    setPendingReview(7);
    setCompletedDisputes(2);
  };

  const handleItemsFlagged = (items: RecordItem[]) => {
    setFlaggedItems(items);
    // Move to generate step
    setDisputeStep('generate');
  };

  const handleBackToReview = () => {
    setDisputeStep('review');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold">HealthScore Disputes</h2>
        <p className="text-muted-foreground">
          Ensure your health data accuracy to maintain an optimal Health Score
        </p>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-orange-600" />
            <div>
              <p className="text-sm text-muted-foreground">Pending Review</p>
              <p className="text-2xl font-bold">{pendingReview}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center space-x-2">
            <FileText className="h-5 w-5 text-blue-600" />
            <div>
              <p className="text-sm text-muted-foreground">Active Disputes</p>
              <p className="text-2xl font-bold">{activeDisputes}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center space-x-2">
            <Clock className="h-5 w-5 text-yellow-600" />
            <div>
              <p className="text-sm text-muted-foreground">Awaiting Response</p>
              <p className="text-2xl font-bold">2</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center space-x-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <div>
              <p className="text-sm text-muted-foreground">Resolved</p>
              <p className="text-2xl font-bold">{completedDisputes}</p>
            </div>
          </div>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="create-dispute" className="flex items-center space-x-2">
            <Plus className="h-4 w-4" />
            <span>Create New Dispute</span>
            {pendingReview > 0 && (
              <Badge variant="destructive" className="ml-1">
                {pendingReview}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="track-disputes" className="flex items-center space-x-2">
            <List className="h-4 w-4" />
            <span>Track My Disputes</span>
            {activeDisputes > 0 && (
              <Badge variant="secondary" className="ml-1">
                {activeDisputes}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="create-dispute">
          <div className="space-y-4">
            {/* Progress Steps */}
            <Card className="p-4">
              <div className="flex items-center space-x-4">
                <div className={`flex items-center space-x-2 ${disputeStep === 'review' ? 'text-blue-600' : 'text-green-600'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm ${disputeStep === 'review' ? 'bg-blue-600' : 'bg-green-600'}`}>
                    1
                  </div>
                  <span className="font-medium">Review Records</span>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
                <div className={`flex items-center space-x-2 ${disputeStep === 'generate' ? 'text-blue-600' : 'text-muted-foreground'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm ${disputeStep === 'generate' ? 'bg-blue-600' : 'bg-gray-400'}`}>
                    2
                  </div>
                  <span className="font-medium">Generate Dispute</span>
                  {flaggedItems.length > 0 && (
                    <Badge variant="default" className="ml-1">
                      {flaggedItems.length}
                    </Badge>
                  )}
                </div>
              </div>
            </Card>

            {disputeStep === 'review' && (
              <RecordReview 
                selectedPatientId={selectedPatientId} 
                onItemsFlagged={handleItemsFlagged}
              />
            )}

            {disputeStep === 'generate' && (
              <div className="space-y-4">
                <Card className="p-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">Generate Dispute Packet</h3>
                    <Button variant="outline" onClick={handleBackToReview}>
                      Back to Review
                    </Button>
                  </div>
                </Card>
                <DisputePacketGenerator 
                  selectedPatientId={selectedPatientId}
                  flaggedItems={flaggedItems}
                />
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="track-disputes">
          <DisputeTracking selectedPatientId={selectedPatientId} />
        </TabsContent>
      </Tabs>
    </div>
  );
};
